import type { ZeroConnectionSnapshot } from "./zero-connection-report"

// Zero prefixes every failure while applying a poke with this. After one, its
// local sources keep the error and rethrow it from every query, so reconnecting
// never heals the tab; only a reload rebuilds them.
export const POKE_FAILURE_PREFIX = "Poke processing error"
export const POKE_RELOAD_GUARD_MS = 5 * 60 * 1000
export const POKE_RELOAD_STORAGE_KEY = "zero-poke-reload-at"
export const STORAGE_LOST_MESSAGE = "Expected IndexedDB not found"

export function isPokeProcessingFailure(
	snapshot: ZeroConnectionSnapshot,
): boolean {
	return (
		snapshot.stateName === "error" &&
		snapshot.reasonKind === "Internal" &&
		(snapshot.reasonMessage?.startsWith(POKE_FAILURE_PREFIX) ?? false)
	)
}

// The browser deleted Zero's IndexedDB mid-session; a reload creates a fresh one.
export function isStorageLostFailure(
	snapshot: ZeroConnectionSnapshot,
	logs: readonly { message: string }[] = [],
): boolean {
	if (snapshot.stateName !== "error") return false
	return [
		snapshot.reasonMessage ?? "",
		...logs.map((entry) => entry.message),
	].some((message) => message.includes(STORAGE_LOST_MESSAGE))
}

// One automatic reload per window, so a failure that returns right after the
// reload leaves the tab on the offline modal instead of looping.
export function claimPokeReload(
	getStorage: () => Pick<Storage, "getItem" | "setItem">,
	now: number,
): boolean {
	try {
		const storage = getStorage()
		const last = Number(storage.getItem(POKE_RELOAD_STORAGE_KEY))
		if (last > 0 && now - last < POKE_RELOAD_GUARD_MS) return false
		storage.setItem(POKE_RELOAD_STORAGE_KEY, String(now))
		return true
	} catch {
		// Without storage the guard can't hold, and a reload loop is worse.
		return false
	}
}
